import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Compass, Tent } from "lucide-react";
import { SOCIAL } from "../lib/social";
import { withUtm, PRINT_UTM } from "../lib/utm";

/**
 * /print/cards — 10 business cards laid out 5 rows × 2 cols on A4 portrait.
 *
 * Standard UK business card: 85mm x 55mm. Cut along the dotted lines after
 * printing on 250gsm card stock for a proper finish (any high-street print
 * shop can do this for ~£15 per 100 cards).
 *
 * Design: matches the Poster brand strip — black + #ff4500, JetBrains Mono
 * for handles, Clash Display for the wordmark. QR code on every card.
 *
 * Tip in the toolbar tells the user EXACTLY which print settings to pick
 * so the cards come out the right size every time.
 */
const PUBLIC_URL =
  (process.env.REACT_APP_PUBLIC_URL || "").trim().replace(/\/$/, "") ||
  "https://camp-compass.uk";

/**
 * QR target URL — UTM-tagged so every business card scan flows
 * through the existing signup-source pipeline and shows up as
 * "businesscard:print:word-of-mouth" in the admin attribution view.
 */
const QR_URL = withUtm(PUBLIC_URL, PRINT_UTM.businessCard);

export default function BusinessCards() {
  // Build a 10-card grid — 5 rows × 2 cols
  const cards = Array.from({ length: 10 });

  return (
    <div className="bg-[#fafafa] min-h-screen print:bg-white" data-testid="cards-page">
      <style>{`
        @page { size: A4; margin: 8mm; }
        @media print {
          html, body { background: #fff !important; }
          .print-hide { display: none !important; }
          .print-canvas { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>

      {/* Toolbar (screen only) */}
      <div className="print-hide max-w-[820px] mx-auto px-6 py-6 flex items-center justify-between">
        <div>
          <div className="eyebrow text-[#ff4500]">Print pack</div>
          <h1 className="font-display text-2xl">Business cards · 10 per A4</h1>
          <p className="text-sm text-[#666] mt-1 max-w-md">
            Standard UK 85×55mm. Print on 250gsm card stock — the dashed lines are cut guides. Margins:
            <strong> Default</strong>. Scale: <strong>100%</strong>. Two-sided not required.
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2"
          data-testid="cards-print-btn"
        >
          Print sheet
        </button>
      </div>

      {/* A4 sheet — 210mm x 297mm with 8mm margin from @page */}
      <div
        className="print-canvas mx-auto bg-white shadow-[0_8px_40px_rgba(0,0,0,0.12)] py-[6mm] px-[10mm]"
        style={{ width: "210mm", minHeight: "297mm" }}
        data-testid="cards-canvas"
      >
        <div
          className="grid gap-x-[8mm] gap-y-[5mm]"
          style={{ gridTemplateColumns: "repeat(2, 85mm)", justifyContent: "center" }}
        >
          {cards.map((_, i) => (
            <Card key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Card() {
  return (
    <div
      className="relative overflow-hidden bg-[#f7f2e8]"
      style={{
        width: "85mm",
        height: "55mm",
        border: "0.5px dashed rgba(0,0,0,0.25)",
        boxShadow: "inset 0 0 0 0.4mm #1f3320",
      }}
    >
      <div className="grid grid-cols-[32mm_1fr_25mm] h-full">
        {/* LEFT — full-bleed photo (no overlay so the image breathes) */}
        <div className="relative">
          <img
            src="/brand/card-hero-v2.jpg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "30% 50%", imageRendering: "auto" }}
          />
          {/* Subtle warm vignette on the right edge so the seam to cream
              feels intentional rather than a hard cut */}
          <div className="absolute inset-y-0 right-0 w-[5mm] bg-gradient-to-l from-[#f7f2e8] to-transparent pointer-events-none" />
        </div>

        {/* MIDDLE — wordmark + tagline on warm cream */}
        <div className="flex flex-col justify-between py-3 pl-3 pr-2 text-[#1f3320]">
          <div>
            <div className="flex items-center gap-1.5">
              <Compass className="w-3.5 h-3.5 text-[#c2410c]" strokeWidth={2} />
              <div className="font-display text-[16px] leading-none tracking-tight text-[#1f3320]">CampCompass</div>
            </div>
            <div className="mono text-[6.5px] uppercase tracking-[0.22em] text-[#1f3320]/65 mt-1.5">UK Edition</div>
            <p className="text-[8.5px] text-[#1f3320]/85 leading-tight mt-2.5 max-w-[42mm]">
              The UK&apos;s honest camper community.
            </p>
            <p className="text-[8.5px] mt-1 max-w-[42mm] font-bold leading-tight text-[#c2410c]">
              All ages, all kit, all welcome.
            </p>
          </div>
          <div className="flex items-center gap-1.5 mono text-[7px] text-[#c2410c] uppercase tracking-[0.14em]">
            <Tent className="w-2.5 h-2.5" strokeWidth={2} />
            <span>Scan · install · pitch</span>
          </div>
        </div>

        {/* RIGHT — QR + URL on solid white so phone cameras lock instantly */}
        <div className="bg-white text-[#1f3320] flex flex-col items-center justify-center py-2 px-1.5 border-l border-[#1f3320]/15">
          <QRCodeSVG
            value={QR_URL}
            size={62}
            level="M"
            bgColor="#ffffff"
            fgColor="#1f3320"
          />
          <div className="mono text-[6px] mt-1 text-[#1f3320]/70 truncate max-w-[24mm] text-center">
            {PUBLIC_URL.replace(/^https?:\/\//, "")}
          </div>
        </div>
      </div>

      {/* Bottom orange accent strip — burnt orange to match cream warmth */}
      <div
        className="absolute left-[32mm] bottom-0 h-[1.5mm] bg-[#c2410c]"
        style={{ right: "25mm" }}
      />
    </div>
  );
}
